import {
  AddAgencyOfficerMutation,
  AdminGetAgencyQuery,
  AgencyOfficerRow,
  GetAgencyOfficerStatsQuery,
  GetAgencyTemplateByDomainQuery,
  InviteAllAgencyLibrariesMutation,
  InviteAgencyLibraryMutation,
  UpdateAgencyDetailsMutation,
  GetCustomSpecialtyBrandingQuery,
  UpdateAgencyOfficerMutation,
  VocabularyInput,
} from '@clipr/lib';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ApiService } from '../../api/apiService';
import {
  AgencySampleConfirmedTemplate,
  AgencySampleUpload,
} from '../../components/agency/agencySampleUpload';
import {
  AgencyDetailsForm,
  createAgencyDetailsForm,
  parseJson,
  toAgencyDetailsInput,
} from '../../components/agencyDetails';

export type AgencyDashboardTemplate = NonNullable<GetAgencyTemplateByDomainQuery['getAgencyTemplateByDomain']>;
export type AgencyDashboardOfficerRow = AgencyOfficerRow & {
  section: 'roster' | 'extras';
};
export type PendingOfficerRow = AgencyOfficerRow & {
  localId: string;
  error?: string;
  fieldErrors: Partial<Record<keyof AgencyOfficerRow, string>>;
};

type AgencyDashboardStore = {
  user?: {
    email: string;
    isAgencySponsor?: boolean;
  } | null;
  apiService: Pick<ApiService,
    'getAgencyTemplateByDomain' |
    'getAgencyOfficerStats' |
    'requestAgencySampleReportUpload' |
    'confirmAgencySampleReportUpload' |
    'inviteAgencyLibrary' |
    'inviteAllAgencyLibraries' |
    'updateAgencyOfficer' |
    'addAgencyOfficer' |
    'updateAgencyDetails' |
    'adminGetAgency' |
    'adminUpdateAgencyVocabulary' |
    'adminUpdateAgencyReportDictionary' |
    'getCustomSpecialtyBranding'>;
};

type AgencyVocabulary = AdminGetAgencyQuery['adminGetAgency']['vocabulary'];
type AgencyReportDictionary = AdminGetAgencyQuery['adminGetAgency']['reportDictionary'];
type AgencyReportFormats = AdminGetAgencyQuery['adminGetAgency']['reportFormats'];
type TrainedFormat = NonNullable<GetCustomSpecialtyBrandingQuery['getCustomSpecialty']>;

type AgencyOfficerStat = GetAgencyOfficerStatsQuery['getAgencyOfficerStats'][number];
type AgencyInviteAllResult = InviteAllAgencyLibrariesMutation['inviteAllAgencyLibraries'];
type AgencyInviteTeam = InviteAgencyLibraryMutation['inviteAgencyLibrary'];
type AgencyUpdateOfficerTemplate = UpdateAgencyOfficerMutation['updateAgencyOfficer'];
type AgencyAddOfficerTemplate = AddAgencyOfficerMutation['addAgencyOfficer'];
type AgencyUpdateDetailsTemplate = UpdateAgencyDetailsMutation['updateAgencyDetails'];

export class AgencyDashboardPageState {

  constructor(readonly store: AgencyDashboardStore) {
    this.sampleUpload = new AgencySampleUpload({
      apiService: store.apiService,
      getContext: () => this.template && this.emailDomain ? {
        orgId: this.template.orgId,
        domain: this.emailDomain,
        sampleReportS3Key: this.template.samplePoliceReportS3Key,
        sourceCustomSpecialtyId: this.template.sourceCustomSpecialtyId,
        trainedFormat: this.trainedFormat,
      } : null,
      onConfirmed: (template) => this.applyConfirmedTemplate(template),
      onTrainingComplete: async (template) => {
        this.template = template;
        await this.loadTrainedFormat();
      },
      refreshTrainedFormat: () => this.loadTrainedFormat(),
    });
    makeObservable(this);
  }

  readonly sampleUpload: AgencySampleUpload;
  @observable template: AgencyDashboardTemplate | null = null;
  @observable officerStats: AgencyOfficerStat[] = [];
  @observable isLoading = false;
  @observable isInvitingAll = false;
  @observable inviteInProgressEmail: string | null = null;
  @observable editInProgressEmail: string | null = null;
  @observable error: string | null = null;
  /**
   * What CLIPr read out of the agency's sample.
   *
   * The sponsor's real question after uploading is not "is that my file" -- the name answers that
   * -- but "did CLIPr understand it". A wrong typeface or a missing letterhead is invisible until
   * someone opens a finished PDF, by which point officers have generated reports in it.
   */
  @observable.ref trainedFormat: TrainedFormat | null = null;
  @observable inviteAllStatus: string | null = null;
  @observable.shallow rowErrors = new Map<string, string>();
  @observable.shallow pendingExtras: PendingOfficerRow[] = [];

  @observable.shallow savedVocabulary: AgencyVocabulary = [];
  @observable isDictionaryOpen = false;
  @observable isSavingVocabulary = false;
  @observable vocabularyError: string | null = null;
  @observable.shallow vocabulary: VocabularyInput[] = [];

  @observable isDetailsOpen = false;
  @observable isSavingDetails = false;
  @observable detailsError: string | null = null;
  @observable.shallow detailsForm: AgencyDetailsForm = createAgencyDetailsForm(null);
  private detailsSaveSeq = 0;
  @observable.shallow savedReportDictionary: AgencyReportDictionary = [];
  @observable savedReportDictionaryVersion = 0;
  @observable.shallow savedReportFormats: AgencyReportFormats = [];
  /**
   * The report format the open editor writes to. Null means the agency-wide fallback, which every
   * format inherits.
   */
  @observable editingSpecialtyId: string | null = null;
  /** Rule count the draft started from, so the clear-everything confirmation judges the right list. */
  private baselineRuleCount = 0;
  /** Generation the open draft was composed against; see the admin page for why it never refreshes. */
  @observable editingReportDictionaryVersion = 0;
  private reportDictionarySaveSeq = 0;
  private confirmedClearReportDictionary = false;
  /** False until adminGetAgency has answered once; editing before then can save over real data. */
  @observable agencyLoaded = false;
  @observable agencyReadError: string | null = null;
  @observable isReportDictionaryOpen = false;
  @observable isSavingReportDictionary = false;
  @observable reportDictionaryError: string | null = null;
  @observable.shallow reportDictionary: { target: string; sourcesText: string }[] = [];

  @computed
  get emailDomain(): string | null {
    return this.store.user?.email.split('@')[1]?.toLowerCase() ?? null;
  }

  @computed
  get officerRows(): AgencyDashboardOfficerRow[] {
    return [
      ...parseRows(this.template?.officers).map((row) => ({ ...row, section: 'roster' as const })),
      ...this.extrasRows,
    ];
  }

  @computed
  get extrasRows(): AgencyDashboardOfficerRow[] {
    return parseRows(this.template?.extras).map((row) => ({ ...row, section: 'extras' as const }));
  }

  @computed
  get officerEmailToTeamId(): Record<string, string> {
    return normalizeEmailMap(this.template?.officerEmailToTeamId);
  }

  @computed
  get invitedCount(): number {
    return Object.keys(this.officerEmailToTeamId).length;
  }

  @computed
  get officerStatsByTeamId(): Record<string, AgencyOfficerStat> {
    return Object.fromEntries(this.officerStats.map((s) => [s.teamId, s]));
  }

  @computed
  get isInviteReady(): boolean {
    return Boolean(this.template?.templateTeamId && this.template?.sourceCustomSpecialtyId);
  }

  @computed
  get agencyName(): string {
    return this.template?.agencyName ?? 'Agency';
  }

  @action
  async mounted() {
    if (!this.store.user?.isAgencySponsor)
      return;

    await this.loadTemplate();
  }

  @action
  unmounted() {
    this.error = null;
    this.sampleUpload.reset();
    // This state object is a store singleton, so a modal left open survives navigation and
    // reopens over stale form values on the way back.
    this.isDetailsOpen = false;
    this.isSavingDetails = false;
    this.detailsError = null;
    // Coming back to the same agency keeps orgId identical, so the org check alone would let a
    // save started before the unmount land on the fresh session. Retire the token instead.
    this.detailsSaveSeq++;
    // Same for the report editor. A draft left open kept its rows while a background load advanced
    // the saved generation underneath it, so the next Save submitted stale rows against a
    // generation the server then accepted.
    this.isReportDictionaryOpen = false;
    this.isSavingReportDictionary = false;
    this.reportDictionaryError = null;
    this.reportDictionary = [];
    this.editingReportDictionaryVersion = 0;
    this.confirmedClearReportDictionary = false;
    this.reportDictionarySaveSeq++;
  }

  @action
  async loadTemplate() {
    if (!this.emailDomain) {
      this.error = 'Admin email domain is missing.';
      return;
    }

    this.isLoading = true;
    this.error = null;

    const [res, err] = await this.store.apiService.getAgencyTemplateByDomain({ domain: this.emailDomain });

    runInAction(() => {
      this.isLoading = false;
      if (err) {
        this.error = err.message;
        return;
      }

      this.template = res?.getAgencyTemplateByDomain ?? null;
      if (!this.template)
        this.error = `No agency template found for ${this.emailDomain}.`;
    });

    if (this.template) {
      void this.loadOfficerStats();
      void this.loadVocabulary();
      void this.loadTrainedFormat();
    }
  }

  @action
  async loadOfficerStats() {
    if (!this.template)
      return;

    const [res] = await this.store.apiService.getAgencyOfficerStats({ orgId: this.template.orgId });

    runInAction(() => {
      this.officerStats = res?.getAgencyOfficerStats ?? [];
    });
  }

  /** Loads the trained format so the page can show what was extracted from the sample. */
  @action
  async loadTrainedFormat() {
    const teamId = this.template?.templateTeamId;
    const specialtyId = this.template?.sourceCustomSpecialtyId;
    if (!teamId || !specialtyId) {
      runInAction(() => { this.trainedFormat = null; });
      return;
    }

    // Never throws. It is called fire-and-forget on mount and after each poll, and the preview is
    // non-critical -- a failed or absent read must not surface as an unhandled rejection (which is
    // what turned every poll tick into a test-suite error and a nonzero CI exit).
    try {
      const [res] = await this.store.apiService.getCustomSpecialtyBranding({ teamId, specialtyId });
      runInAction(() => {
        this.trainedFormat = res?.getCustomSpecialty ?? null;
      });
    } catch {
      runInAction(() => { this.trainedFormat = null; });
    }
  }

  @action
  async loadVocabulary() {
    if (!this.template)
      return;

    // adminGetAgency is authorized for the owning sponsor (assertCanManageAgency);
    // we read the vocabulary and report dictionary slices here.
    // Bound to the page session: a load that lands after the sponsor navigated away must not
    // advance the saved generation under a draft belonging to the previous visit.
    const seq = this.reportDictionarySaveSeq;
    const [res, err] = await this.store.apiService.adminGetAgency({ orgId: this.template.orgId });

    runInAction(() => {
      // A failed read must not read as "this agency has nothing". Both editors save the whole array,
      // so opening one on a defaulted-empty list and pressing Save deletes everything the agency had.
      // Leave the last known state alone and record that we never got an authoritative answer.
      if (seq !== this.reportDictionarySaveSeq) return;
      this.agencyReadError = err ? err.message : null;
      if (err || !res) return;
      this.savedVocabulary = res.adminGetAgency.vocabulary;
      this.savedReportDictionary = res.adminGetAgency.reportDictionary;
      this.savedReportDictionaryVersion = res.adminGetAgency.reportDictionaryVersion;
      this.savedReportFormats = res.adminGetAgency.reportFormats ?? [];
      this.agencyLoaded = true;
    });
  }

  @action
  openDictionary() {
    this.vocabulary = this.savedVocabulary.map(v => ({
      phrase: v.phrase,
      displayAs: v.displayAs ?? undefined,
    }));
    this.vocabularyError = null;
    this.isDictionaryOpen = true;
  }

  @action
  closeDictionary() {
    if (this.isSavingVocabulary) return;
    this.isDictionaryOpen = false;
    this.vocabularyError = null;
  }

  @action
  openDetails() {
    this.detailsForm = createAgencyDetailsForm(this.template);
    this.detailsError = null;
    this.isDetailsOpen = true;
  }

  @action
  closeDetails() {
    if (this.isSavingDetails) return;
    this.isDetailsOpen = false;
    this.detailsError = null;
  }

  @action
  updateDetails(patch: Partial<AgencyDetailsForm>) {
    this.detailsForm = { ...this.detailsForm, ...patch };
  }

  @action
  async submitDetails() {
    const orgId = this.template?.orgId;
    if (!orgId) return;

    const [details, validationError] = toAgencyDetailsInput(this.detailsForm);
    if (!details) {
      this.detailsError = validationError;
      return;
    }

    // This state is a store singleton, so an in-flight save survives leaving the page. Without a
    // token its completion would reopen/close a modal and patch a template the user has moved on
    // from — or belong to an agency they are no longer looking at.
    const seq = ++this.detailsSaveSeq;
    this.isSavingDetails = true;
    this.detailsError = null;

    const [res, err] = await this.store.apiService.updateAgencyDetails({ orgId, details });

    runInAction(() => {
      if (seq !== this.detailsSaveSeq || orgId !== this.template?.orgId)
        return;
      this.isSavingDetails = false;
      if (err) {
        this.detailsError = err.message;
        return;
      }
      // Nothing to merge means the grid would keep rendering pre-save values behind a closed
      // modal, which reads as a silent success. Keep it open and say so.
      if (!res?.updateAgencyDetails) {
        this.detailsError = 'Agency details did not come back from the server. Reload and try again.';
        return;
      }
      this.applyUpdatedDetailsTemplate(res.updateAgencyDetails);
      this.isDetailsOpen = false;
    });
  }

  @action
  addVocabularyRow() {
    this.vocabulary = [...this.vocabulary, { phrase: '', displayAs: '' }];
  }

  @action
  updateVocabularyRow(index: number, patch: Partial<VocabularyInput>) {
    this.vocabulary = this.vocabulary.map((row, i) => (i === index ? { ...row, ...patch } : row));
  }

  @action
  removeVocabularyRow(index: number) {
    this.vocabulary = this.vocabulary.filter((_, i) => i !== index);
  }

  @action
  async submitVocabulary() {
    if (!this.template) return;

    const vocabulary = this.vocabulary
      .map(row => ({
        phrase: row.phrase.trim(),
        displayAs: row.displayAs?.trim() || undefined,
      }))
      .filter(row => row.phrase);

    const phrases = vocabulary.map(v => v.phrase.toLowerCase());
    if (phrases.length !== new Set(phrases).size) {
      this.vocabularyError = 'Duplicate phrases are not allowed';
      return;
    }

    this.isSavingVocabulary = true;
    this.vocabularyError = null;

    const [res, err] = await this.store.apiService.adminUpdateAgencyVocabulary({
      orgId: this.template.orgId,
      vocabulary,
    });

    runInAction(() => {
      this.isSavingVocabulary = false;
      if (err) {
        this.vocabularyError = err.message;
        return;
      }
      this.savedVocabulary = res?.adminUpdateAgencyVocabulary.vocabulary ?? [];
      this.isDictionaryOpen = false;
    });
  }

  @computed
  get canEditReportDictionary(): boolean {
    // Rules now live on the agency itself, which always exists, so a trained format is no longer a
    // precondition -- an agency that trained through the Train wizard has terminology and nowhere
    // else to record it. The read guards stay: the editor saves the whole array, so opening it on a
    // dictionary we never actually read and pressing Save deletes every rule the agency had.
    return this.agencyLoaded && !this.agencyReadError;
  }

  @action
  openReportDictionary(specialtyId: string | null = null) {
    if (!this.canEditReportDictionary) return;
    this.loadReportDictionaryFor(specialtyId);
    this.isReportDictionaryOpen = true;
  }

  /** Switch the open editor to another format. Discards the draft — it belonged to the other one. */
  @action
  selectReportDictionaryFormat(specialtyId: string | null) {
    if (this.isSavingReportDictionary) return;
    this.loadReportDictionaryFor(specialtyId);
  }

  @action
  private loadReportDictionaryFor(specialtyId: string | null) {
    // An id that names no format of this agency falls back to the agency-wide list rather than
    // opening an editor over rules it would then save somewhere else.
    const format = specialtyId
      ? this.savedReportFormats.find(f => f.specialtyId === specialtyId)
      : undefined;
    const rules = format?.reportDictionary ?? this.savedReportDictionary;

    this.reportDictionary = rules.map(rule => ({
      target: rule.target,
      sourcesText: rule.sources.join(', '),
    }));
    this.editingSpecialtyId = format?.specialtyId ?? null;
    this.editingReportDictionaryVersion = format
      ? format.reportDictionaryVersion
      : this.savedReportDictionaryVersion;
    this.baselineRuleCount = rules.length;
    this.reportDictionaryError = null;
    this.confirmedClearReportDictionary = false;
  }

  @action
  closeReportDictionary() {
    if (this.isSavingReportDictionary) return;
    this.isReportDictionaryOpen = false;
    this.reportDictionaryError = null;
  }

  @action
  addReportDictionaryRow() {
    this.reportDictionary = [...this.reportDictionary, { target: '', sourcesText: '' }];
  }

  @action
  updateReportDictionaryRow(index: number, patch: Partial<{ target: string; sourcesText: string }>) {
    this.reportDictionary = this.reportDictionary.map((row, i) => (i === index ? { ...row, ...patch } : row));
  }

  @action
  removeReportDictionaryRow(index: number) {
    this.reportDictionary = this.reportDictionary.filter((_, i) => i !== index);
  }

  @action
  async submitReportDictionary() {
    if (!this.template) return;

    // Rows are rejected, never silently dropped. A whole-array save means a row the user can still
    // see on screen but that normalizes to nothing would delete that rule without ever saying so.
    const rows = this.reportDictionary.map(row => ({
      target: row.target.trim(),
      // Either separator. A comma is what people type and what the table shows; newlines make a
      // list pasted from a document work without reformatting. Neither can occur inside a term --
      // validation rejects control characters, and a term here is a single common noun, not a name
      // (names belong in the transcript dictionary, where commas are left alone).
      sources: row.sourcesText.split(/[\n,]/).map(s => s.trim()).filter(Boolean),
    }));

    if (rows.some(row => !row.target || row.sources.length === 0)) {
      this.reportDictionaryError = 'Every rule needs a replacement term and at least one term to replace';
      return;
    }

    // NFKC first, matching the server; see the admin page.
    const targets = rows.map(r => r.target.normalize('NFKC').toLowerCase());
    if (targets.length !== new Set(targets).size) {
      this.reportDictionaryError = 'Duplicate targets are not allowed';
      return;
    }


    // A whole-array save, so submitting an empty editor deletes every rule the agency had. The one
    // edit here that is destructive rather than corrective, and the one worth confirming.
    if (rows.length === 0 && this.baselineRuleCount > 0 && !this.confirmedClearReportDictionary) {
      this.reportDictionaryError = 'This removes every report rule. Save again to confirm.';
      this.confirmedClearReportDictionary = true;
      return;
    }

    this.isSavingReportDictionary = true;
    this.reportDictionaryError = null;

    const seq = this.reportDictionarySaveSeq;
    const [res, err] = await this.store.apiService.adminUpdateAgencyReportDictionary({
      orgId: this.template.orgId,
      reportDictionary: rows,
      expectedVersion: this.editingReportDictionaryVersion,
      specialtyId: this.editingSpecialtyId ?? undefined,
    });

    if (seq !== this.reportDictionarySaveSeq) return;

    runInAction(() => {
      this.isSavingReportDictionary = false;
      if (err) {
        this.reportDictionaryError = err.message;
        return;
      }
      const saved = res?.adminUpdateAgencyReportDictionary;
      // Cached against the list the EDITOR was on, not the one the server names: an agency-wide save
      // at an agency with a registered format lands on that format and is mirrored to the agency, so
      // the response carries a format id while the list on screen is the agency-wide one. Update the
      // wrong one and the editor reopens on pre-save rows whose generation reads as someone else's
      // edit. The returned id still updates its row, so the formats table stays truthful.
      if (!this.editingSpecialtyId) {
        this.savedReportDictionary = saved?.reportDictionary ?? [];
        this.savedReportDictionaryVersion = saved?.reportDictionaryVersion ?? 0;
      }
      if (saved) {
        this.savedReportFormats = this.savedReportFormats.map(format => (
          format.specialtyId === saved.specialtyId
            ? {
              ...format,
              reportDictionary: saved.reportDictionary,
              reportDictionaryVersion: saved.reportDictionaryVersion,
            }
            : format
        ));
      }
      this.isReportDictionaryOpen = false;
    });

    // Only on failure. The success path already carries what landed, and re-reading it cost a
    // full agency aggregation -- Elasticsearch plus a query per library -- for a mutation that had
    // already told us everything it changed.
    if (err) await this.loadVocabulary();
  }


  @action
  async editOfficer(row: AgencyDashboardOfficerRow, updates: Partial<AgencyOfficerRow>) {
    if (!this.template)
      return;

    const currentEmail = row.email;
    const currentEmailKey = normalizeEmail(currentEmail);

    const next: AgencyOfficerRow = {
      firstName: row.firstName,
      lastName: row.lastName,
      title: row.title,
      email: row.email,
      supervisorTitle: row.supervisorTitle,
      supervisorName: row.supervisorName,
      supervisorEmail: row.supervisorEmail,
      ...updates,
    };

    const newEmailKey = normalizeEmail(next.email);
    if (!newEmailKey)
      return;

    this.rowErrors.delete(currentEmailKey);
    this.rowErrors.delete(newEmailKey);
    this.editInProgressEmail = currentEmailKey;

    const [res, err] = await this.store.apiService.updateAgencyOfficer({
      orgId: this.template.orgId,
      currentEmail,
      officer: next,
    });

    runInAction(() => {
      this.editInProgressEmail = null;
      if (err) {
        this.rowErrors.set(currentEmailKey, err.message);
        return;
      }

      this.applyUpdatedRosterTemplate(res?.updateAgencyOfficer);
    });
  }

  @action
  addPendingOfficer() {
    this.pendingExtras.push({
      localId: createLocalId(),
      firstName: '',
      lastName: '',
      title: '',
      email: '',
      supervisorTitle: '',
      supervisorName: '',
      supervisorEmail: '',
      fieldErrors: {},
    });
  }

  @action
  editPendingOfficer(localId: string, updates: Partial<AgencyOfficerRow>) {
    const index = this.pendingExtras.findIndex((r) => r.localId === localId);
    if (index === -1)
      return;

    const next = {
      ...this.pendingExtras[index],
      ...updates,
      error: undefined,
    };
    next.fieldErrors = this.validateRow(next);
    this.pendingExtras[index] = next;
  }

  @action
  removePendingOfficer(localId: string) {
    this.pendingExtras = this.pendingExtras.filter((r) => r.localId !== localId);
  }

  @action
  async commitPendingOfficer(localId: string) {
    if (!this.template)
      return;

    const row = this.pendingExtras.find((r) => r.localId === localId);
    if (!row)
      return;

    const fieldErrors = this.validateRow(row);
    if (Object.keys(fieldErrors).length > 0 || !isOfficerComplete(row)) {
      const index = this.pendingExtras.findIndex((r) => r.localId === localId);
      if (index !== -1)
        this.pendingExtras[index] = { ...row, fieldErrors };
      return;
    }

    const officer: AgencyOfficerRow = {
      firstName: row.firstName.trim(),
      lastName: row.lastName.trim(),
      title: row.title.trim(),
      email: row.email.trim().toLowerCase(),
      supervisorTitle: row.supervisorTitle.trim(),
      supervisorName: row.supervisorName.trim(),
      supervisorEmail: row.supervisorEmail.trim().toLowerCase(),
    };

    const [res, requestErr] = await this.store.apiService.addAgencyOfficer({
      orgId: this.template.orgId,
      officer,
    });

    runInAction(() => {
      if (requestErr) {
        const index = this.pendingExtras.findIndex((r) => r.localId === localId);
        if (index !== -1)
          this.pendingExtras[index] = { ...this.pendingExtras[index], error: requestErr.message };
        return;
      }

      this.applyAddedTemplate(res?.addAgencyOfficer);
      this.pendingExtras = this.pendingExtras.filter((r) => r.localId !== localId);
    });
  }

  validateRow(row: AgencyOfficerRow): Partial<Record<keyof AgencyOfficerRow, string>> {
    const errors: Partial<Record<keyof AgencyOfficerRow, string>> = {};
    const domain = this.template?.domain ?? '';

    const checkEmail = (key: 'email' | 'supervisorEmail', value: string) => {
      if (!value)
        return;

      if (!/^\S+@\S+\.\S+$/.test(value)) {
        errors[key] = 'Not a valid email';
        return;
      }

      const valueDomain = value.split('@')[1]?.toLowerCase() ?? '';
      if (domain && valueDomain !== domain.toLowerCase())
        errors[key] = `Email must end with @${domain}`;
    };
    checkEmail('email', row.email);
    checkEmail('supervisorEmail', row.supervisorEmail);

    const emailKey = row.email.trim().toLowerCase();
    if (emailKey) {
      const existing = this.officerRows.find((r) => r.email.trim().toLowerCase() === emailKey);
      const otherPending = this.pendingExtras.find((r) =>
        'localId' in r && r.localId !== (row as PendingOfficerRow).localId &&
        r.email.trim().toLowerCase() === emailKey);
      if (existing || otherPending)
        errors.email = 'Already used by another officer';
    }

    return errors;
  }

  @action
  async inviteOfficer(row: AgencyDashboardOfficerRow) {
    if (!this.template)
      return;

    const emailKey = normalizeEmail(row.email);
    this.inviteInProgressEmail = emailKey;
    this.rowErrors.delete(emailKey);

    const [res, err] = await this.store.apiService.inviteAgencyLibrary({
      orgId: this.template.orgId,
      officer: toOfficerInput(row),
    });

    runInAction(() => {
      this.inviteInProgressEmail = null;
      if (err) {
        this.rowErrors.set(emailKey, err.message);
        return;
      }

      this.markOfficerInvited(row.email, res?.inviteAgencyLibrary);
    });

    if (!err)
      await this.loadOfficerStats();
  }

  @action
  async inviteAll() {
    if (!this.template)
      return;

    this.isInvitingAll = true;
    this.inviteAllStatus = null;
    this.rowErrors.clear();

    const [res, err] = await this.store.apiService.inviteAllAgencyLibraries({ orgId: this.template.orgId });

    runInAction(() => {
      this.isInvitingAll = false;
      if (err) {
        this.error = err.message;
        return;
      }

      this.applyInviteAllResult(res?.inviteAllAgencyLibraries);
    });

    if (!err)
      void this.loadOfficerStats();
  }

  @action
  private applyConfirmedTemplate(template?: AgencySampleConfirmedTemplate) {
    if (!this.template || !template)
      return;

    this.template = {
      ...this.template,
      sourceCustomSpecialtyId: template.sourceCustomSpecialtyId,
      samplePoliceReportS3Key: template.samplePoliceReportS3Key,
      templateTeamId: template.templateTeamId,
      version: template.version,
    };
  }

  @action
  private applyAddedTemplate(template?: AgencyAddOfficerTemplate) {
    if (!this.template || !template)
      return;

    this.template = {
      ...this.template,
      extras: template.extras,
      version: template.version,
      updatedAt: template.updatedAt,
    };
  }

  @action
  private applyUpdatedRosterTemplate(template?: AgencyUpdateOfficerTemplate) {
    if (!this.template || !template)
      return;

    this.template = {
      ...this.template,
      officers: template.officers,
      officerEmailToTeamId: template.officerEmailToTeamId,
      version: template.version,
      updatedAt: template.updatedAt,
    };
  }

  @action
  private applyUpdatedDetailsTemplate(template?: AgencyUpdateDetailsTemplate) {
    if (!this.template || !template)
      return;

    this.template = {
      ...this.template,
      agencyName: template.agencyName,
      swornOfficersCount: template.swornOfficersCount,
      program: template.program,
      version: template.version,
      updatedAt: template.updatedAt,
    };
  }

  @action
  private markOfficerInvited(email: string, team?: AgencyInviteTeam) {
    if (!this.template || !team)
      return;

    this.template = {
      ...this.template,
      officerEmailToTeamId: {
        ...this.officerEmailToTeamId,
        [normalizeEmail(email)]: team.id,
      },
    };
    this.inviteAllStatus = `Invited ${email}`;
  }

  @action
  private applyInviteAllResult(result?: AgencyInviteAllResult) {
    const teamsCount = result?.teams.length ?? 0;
    this.inviteAllStatus = `${teamsCount} ${teamsCount === 1 ? 'library' : 'libraries'} invited`;

    for (const error of result?.errors ?? [])
      this.rowErrors.set(normalizeEmail(error.officerEmail), error.message);
  }
}

function parseRows(value: unknown): AgencyOfficerRow[] {
  const parsed = parseJson(value);
  if (!Array.isArray(parsed))
    return [];

  return parsed.filter(isOfficerRow);
}

function isOfficerRow(value: unknown): value is AgencyOfficerRow {
  if (!value || typeof value !== 'object')
    return false;

  const row = value as Record<string, unknown>;
  return typeof row.email === 'string';
}

function normalizeEmailMap(value: unknown): Record<string, string> {
  const parsed = parseJson(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
    return {};

  return Object.fromEntries(
    Object.entries(parsed as Record<string, unknown>)
      .filter(([, teamId]) => typeof teamId === 'string')
      .map(([email, teamId]) => [normalizeEmail(email), teamId as string]));
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function toOfficerInput(row: AgencyDashboardOfficerRow): AgencyOfficerRow {
  return {
    firstName: row.firstName,
    lastName: row.lastName,
    title: row.title,
    email: row.email,
    supervisorTitle: row.supervisorTitle,
    supervisorName: row.supervisorName,
    supervisorEmail: row.supervisorEmail,
  };
}

function createLocalId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function isOfficerComplete(row: AgencyOfficerRow): boolean {
  return Boolean(
    row.firstName.trim() &&
    row.lastName.trim() &&
    row.title.trim() &&
    row.email.trim() &&
    row.supervisorTitle.trim() &&
    row.supervisorName.trim() &&
    row.supervisorEmail.trim(),
  );
}
